import React, { useMemo } from "react";
import { Popup, Icon } from "semantic-ui-react";
import { formatValueAsCurrency } from "../../components/shared/utils";
import { RestrictionTypesValues, RestrictionTypes } from "../../components/shared/constants";
import TextList from "../../components/shared/TextList";
import { buildAutoDescriptionForPolicy } from "../../components/shared/reservationPolicy";
import { isLightColor, RESTRICTION_COLORS, resolveAvailableWithOB, formatOccupancy } from "./DayCell";
import AvailabilityCenter from "./AvailabilityCenter";

// The room type line is the last search's data; once the dropdown no longer holds that
// room type (cleared or changed, not searched yet) the line goes away.
export const roomTypeLineName = (searchedRoomTypeId, selectedRoomTypeId, name) =>
    searchedRoomTypeId != null && selectedRoomTypeId != null && String(searchedRoomTypeId) === String(selectedRoomTypeId)
        ? name
        : null;

/**
 * HS-70186: display-only counterpart to DayCell, for the grid flow. All the
 * derivations DayCell does client-side (aggregation, restriction/policy/event
 * lookups, forecast math) are already done server-side by MonthlyGridBuilder -
 * this component only formats the resulting cell for the same markup/classes as
 * DayCell (L317-489), so GridDayCell.test.jsx / MonthlyParity.test.jsx can compare
 * normalized HTML between the two. No fetches, no business logic.
 */
const GridDayCell = React.memo(({ date, cell, policy, allocationDates, text, message, currentHotel, availableMode, roomTypeName, afterTax, showPrices, selected, onSelect }) => {
    const avail = cell?.Availability;
    const rawSold = avail?.Sold;
    const rawAvailable = avail?.Available;
    const rawGI = avail?.GrossInventory;
    const rawOos = avail?.Oos;
    const rawOoo = avail?.Ooo;

    const Sold = rawSold ?? 0;
    const I = rawGI ?? 0;
    const Oos = rawOos ?? 0;
    const Denom = Math.max(0, I - Oos);
    // HS-70658: same rules as DayCell - plain Available, not clamped, red at <= 0; OB shown separately.
    const AvailableDisplay = rawAvailable ?? 0;
    const Overbooking = Math.max(0, (resolveAvailableWithOB(avail) ?? 0) - AvailableDisplay);
    const isAllOos = avail != null && Denom <= 0 && I > 0;
    const isFull = rawAvailable != null && rawAvailable <= 0 && I > 0;

    // As Optima Live's room type row: the hotel numbers above stay hotel level, this line is the filtered room type.
    const rtAvail = cell?.RoomTypeAvailability;
    const rtNumerator = availableMode ? (rtAvail?.Available ?? 0) : (rtAvail?.Sold ?? 0);
    const rtDenom = Math.max(0, (rtAvail?.GrossInventory ?? 0) - (rtAvail?.Oos ?? 0));
    const outOfTextRaw = text("OutOf");
    const outOfLabel = (outOfTextRaw && !outOfTextRaw.startsWith("[")) ? outOfTextRaw : "out of";

    const ER = cell?.EffectiveRestriction ?? RestrictionTypes.OpenRestrictionType;
    const isUniform = cell?.IsUniform ?? true;
    const minStay = cell?.MinLengthOfStay;
    const maxStay = cell?.MaxLengthOfStay;

    const restrictionBadgeColor = useMemo(
        () => RESTRICTION_COLORS[RestrictionTypesValues[ER]?.color] || '#8bc34a',
        [ER]
    );

    const P = cell?.Price;
    const notes = cell?.Notes;
    // Event colour/position/label are resolved server-side (MonthlyGridBuilder),
    // mirroring DayCell's eventColorMap + buildEventSpans.
    const event = cell?.Event;
    const eventName = event?.Name;
    const calendarColor = event?.Color || null;
    const eventPosition = event?.Position || 'single';

    // `policy` is gridResult.Policies[cell.PolicyId] - same {Id, Name, CancellationPolicies,
    // DepositPolicies} shape as legacy data.ReservationPolicies entries.
    const policyDescription = policy
        ? buildAutoDescriptionForPolicy(policy, message, text, currentHotel?.HotelParameter?.TimeZone)
        : null;

    // Same A/U/L/P sums as DayCell, over the selected allocations' dates for this day.
    const AL = allocationDates && allocationDates.length > 0 && allocationDates.reduce((acc, curr) => {
        acc.A += curr.AllocatedRooms;
        acc.U += curr.ReservedRooms;
        acc.L += curr.LeftRooms;
        acc.P += curr.PresentedRooms;
        return acc;
    }, { A: 0, U: 0, L: 0, P: 0 });

    const forecastPercent = cell?.ForecastPercent;
    const forecastTextRaw = text("Forecast");
    const forecastLabel = (forecastTextRaw && !forecastTextRaw.startsWith("[")) ? forecastTextRaw : "Forecast";

    const cellClass = `day-cell${selected ? ' day-cell--selected' : ''}${isFull ? ' day-cell--full' : ''}`;

    return (
        <div className={cellClass} onClick={() => onSelect?.(date)}>
            <div className="day-cell-header">
                <span className="day-number">{date.getDate()}</span>
                {policy && (
                    <Popup
                        trigger={
                            <span className="policy-badge">
                                <Icon name="sticky note outline" size="small" />
                                {policy.Name}
                            </span>
                        }
                        content={
                            <div style={{ padding: 10 }}>
                                {policyDescription
                                    ? <TextList text={policyDescription} />
                                    : <span>{policy.Name}</span>
                                }
                            </div>
                        }
                    />
                )}
                <div className="day-cell-icons">
                    <Popup
                        trigger={
                            <span
                                className={`restriction-badge${!isUniform ? ' restriction-badge--other' : ''}`}
                                style={!isUniform
                                    ? { borderColor: restrictionBadgeColor, color: restrictionBadgeColor }
                                    : { backgroundColor: restrictionBadgeColor }
                                }
                            >
                                {RestrictionTypesValues[ER]?.value}
                            </span>
                        }
                        content={text(RestrictionTypesValues[ER]?.key)}
                    />
                    {notes != null && (
                        <Popup content={notes} trigger={<Icon name="comment" size="small" className="cell-action cell-active" />} />
                    )}
                </div>
            </div>

            <AvailabilityCenter
                availableMode={availableMode}
                AvailableDisplay={AvailableDisplay}
                Overbooking={Overbooking}
                Sold={Sold}
                Denom={Denom}
                isAllOos={isAllOos}
                I={I}
                Oos={Oos}
                rawOoo={rawOoo}
                text={text}
            />

            {rtAvail && roomTypeName && (
                <div className={`day-cell-roomtype${availableMode && rtNumerator <= 0 ? ' day-cell-roomtype--full' : ''}`}>
                    <span className="roomtype-name">{roomTypeName}</span>
                    <span className="roomtype-count">{rtNumerator} {outOfLabel} {rtDenom}</span>
                </div>
            )}

            <div className="day-cell-metrics">
                <Popup
                    trigger={
                        <span className="yield-badge">
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5 8L1.5 2H8.5L5 8Z" fill="#a142ff" />
                            </svg>
                            Y={cell?.YieldLevel || 0}
                        </span>
                    }
                    content={text("Yield")}
                />
                <Popup
                    trigger={<span className="occ-text">{formatOccupancy(Sold, Denom)}% {text("Occupancy") || "Occ."}</span>}
                    content={text("Occupancy")}
                />
                <Popup
                    trigger={<span className="forecast-text">{forecastPercent != null ? forecastPercent.toFixed(0) : 0}% {forecastLabel}</span>}
                    content={forecastLabel}
                />
            </div>

            {(minStay > 1 || (maxStay > 0 && maxStay < 999)) && (
                <div className="day-cell-minstay">
                    <Icon name="moon outline" size="small" className="moon-icon" />
                    {minStay > 1 && (
                        <div className="stay-box">
                            <span className="stay-value">{minStay}</span>
                            <span className="stay-label">Min</span>
                        </div>
                    )}
                    {maxStay > 0 && maxStay < 999 && (
                        <div className="stay-box">
                            <span className="stay-value">{maxStay}</span>
                            <span className="stay-label">Max</span>
                        </div>
                    )}
                </div>
            )}

            {showPrices && P && (
                <div className="day-cell-prices">
                    <Popup
                        trigger={
                            <div className="price-row">
                                <span className="price-label">
                                    <Icon name="bed" size="small" />
                                    S
                                </span>
                                <span className="price-value">
                                    {formatValueAsCurrency(afterTax ? P.PricePerPaxAfterTax?.Single : P.PricePerPaxBeforeTax?.Single, P.CurrencyId)}
                                </span>
                            </div>
                        }
                        content={[P.RoomTypeName, P.RateName, P.Name].filter(Boolean).join(' / ')}
                    />
                    <Popup
                        trigger={
                            <div className="price-row">
                                <span className="price-label">
                                    <Icon name="bed" size="small" />
                                    D
                                </span>
                                <span className="price-value">
                                    {formatValueAsCurrency(afterTax ? P.PricePerPaxAfterTax?.Double : P.PricePerPaxBeforeTax?.Double, P.CurrencyId)}
                                </span>
                            </div>
                        }
                        content={[P.RoomTypeName, P.RateName, P.Name].filter(Boolean).join(' / ')}
                    />
                </div>
            )}

            {AL && (
                <div className="day-cell-allocations">
                    <Popup
                        trigger={
                            <div className="alloc-row">
                                <span className="alloc-label">A/U/L</span>
                                <span className="alloc-value">{AL.A || 0}/{AL.U || 0}/{AL.L || 0}</span>
                            </div>
                        }
                        content={
                            <div style={{ padding: "8px" }}>
                                Allocated: {AL.A || 0}<br />
                                Used: {AL.U || 0}<br />
                                Left: {AL.L || 0}<br />
                                Presented: {AL.P || 0}
                            </div>
                        }
                    />
                </div>
            )}

            <div className={`day-cell-event ${eventName ? `event-${eventPosition}` : 'event-placeholder'}`}>
                {eventName && (
                    <Popup
                        trigger={<span className="event-name" style={calendarColor ? { backgroundColor: calendarColor, color: isLightColor(calendarColor) ? '#1a1a1a' : '#fff' } : {}}>{event.ShowLabel === false ? ' ' : eventName}</span>}
                        content={eventName}
                    />
                )}
            </div>
        </div>
    );
});

export default GridDayCell;
